import { useCallback, useEffect, useReducer, useState } from "react";

export type ConnectHandler = (ev: Event) => void;
export type MessageHandler<T> = (ev: MessageEvent<T>) => void;
export type DisconnectHandler = (ev: CloseEvent) => void;

export type ConnectFN = (wsUri: string) => void;
export type SendMessageFN<T> = (message: T) => void;
export type CloseFN = () => void;

export type SessionHook<T> = [ConnectFN, SendMessageFN<T>, CloseFN];
export type PauseHandlerHook = [(fn: ConnectFN) => void, () => void];

export function useWebsocketConnection<SentMessageType>(
  onOpen: ConnectHandler,
  onMessage: MessageHandler<string>,
  onClose: DisconnectHandler
): SessionHook<SentMessageType> {
  const [session, setSession] = useState<WebSocket | null>(null);

  const updateOpenHandler = () => {
    if (!session) return;
    session.addEventListener("open", onOpen);
    return () => {
      session.removeEventListener("open", onOpen);
    };
  };

  const updateMessageHandler = () => {
    if (!session) return;
    session.addEventListener("message", onMessage);
    return () => {
      session.removeEventListener("message", onMessage);
    };
  };

  const updateCloseHandler = () => {
    if (!session) return;
    session.addEventListener("close", onClose);
    return () => {
      session.removeEventListener("close", onClose);
    };
  };

  useEffect(updateOpenHandler, [session, onOpen]);
  useEffect(updateMessageHandler, [session, onMessage]);
  useEffect(updateCloseHandler, [session, onClose]);

  const connect: ConnectFN = useCallback(
    (wsUri: string) => {
      if (session) return;
      const ws = new WebSocket(wsUri);
      setSession(ws);
    },
    [session]
  );

  const sendMessage: SendMessageFN<SentMessageType> = (args: SentMessageType) => {
    if (!session) return;
    session.send(JSON.stringify(args));
  };

  const close: CloseFN = useCallback(() => {
    if (!session) return;
    if (session.readyState === session.OPEN) {
      session.close();
    }
    setSession(null);
  }, [session]);

  return [connect, sendMessage, close];
}

export type ReducerFunction<StateType, MessageType> = (
  state: StateType,
  message: MessageType
) => StateType;

export function useWebsocket<StateType, ReceivedMessageType, SentMessageType>(
  wsUri: string,
  initialState: StateType,
  reducer: ReducerFunction<StateType, ReceivedMessageType>
): [StateType, SendMessageFN<SentMessageType>] {
  const [state, dispatch] = useReducer(reducer, initialState);

  const receiveMessageFn = useCallback(
    (message: string) => dispatch(JSON.parse(message) as ReceivedMessageType),
    []
  );

  const [connect, sendMessage, close] = useWebsocketConnection(
    () => null,
    (ev: MessageEvent<string>) => receiveMessageFn(ev.data),
    () => null
  );

  useEffect(() => {
    connect(wsUri);
    return () => close();
  }, [connect, wsUri, close]);

  return [state, sendMessage];
}

export function useWebsocketReadonly<StateType, ReceivedMessageType>(
  wsUri: string,
  initialState: StateType,
  reducer: ReducerFunction<StateType, ReceivedMessageType>
): [StateType] {
  const [state] = useWebsocket(wsUri, initialState, reducer);
  return [state];
}
